import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import { 
  LayoutDashboard, ShoppingCart, Package, BarChart3, 
  Search, Truck, RefreshCw, ArrowUpRight, Save, Eye, X, AlertTriangle, Users
} from 'lucide-react';

// ✅ BASE URL
const BASE_URL = API_BASE_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('parosa_token');
  const isAdminUser = user && (user.role === 'admin' || user.isAdmin === true);

  // --- STATE ---
  const [activeSection, setActiveSection] = useState('dashboard'); 
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]); // ✅ NEW: Requests State
  const [loading, setLoading] = useState(true);
  const [orderTab, setOrderTab] = useState('All'); 
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [selectedOrder, setSelectedOrder] = useState(null); 

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (!token) {
        setLoading(false);
        return;
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // ✅ ADDED REQUESTS FETCH
      const [ordersRes, productsRes, requestsRes] = await Promise.all([
        axios.get(`${BASE_URL}/orders/all`, config),
        axios.get(`${BASE_URL}/products`),
        axios.get(`${BASE_URL}/membership-requests`, config).catch(() => ({ data: [] })) // Handle potential missing endpoint
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setRequests(requestsRes.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching admin data:", err);
      setLoading(false);
    }
  }, [token]);

  // --- INITIAL DATA FETCH ---
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- ACTIONS ---
  const handleShip = async (orderId) => {
    if (!window.confirm("Push this order to Shiprocket for pickup?")) return;
    try {
      const res = await axios.post(
        `${BASE_URL}/shipping/create-order/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const updatedOrder = res.data.order;
        if (updatedOrder) {
          setOrders(prev => prev.map(o => o._id === updatedOrder._id ? updatedOrder : o));
          if (selectedOrder && selectedOrder._id === updatedOrder._id) {
            setSelectedOrder(updatedOrder);
          }
        } else {
          fetchData();
        }
        alert(res.data.message || "Shipment created");
      }
    } catch (err) {
      alert("Shipping Failed: " + (err.response?.data?.message || err.message));
    }
  };

  const handleSyncTracking = async (orderId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/shipping/track/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedOrder = res.data.order;
      if (updatedOrder) {
        setOrders(prev => prev.map(o => o._id === updatedOrder._id ? updatedOrder : o));
        if (selectedOrder && selectedOrder._id === updatedOrder._id) {
          setSelectedOrder(updatedOrder);
        }
      } else {
        fetchData();
      }
    } catch (err) {
      alert("Tracking sync failed: " + (err.response?.data?.message || err.message));
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${BASE_URL}/orders/${orderId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Optimistic Update
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status: newStatus } : o));
      
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const handleStockUpdate = async (id, newStock) => {
    try {
      await axios.put(
        `${BASE_URL}/products/${id}`,
        { countInStock: newStock },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(prev => prev.map(p => p._id === id ? { ...p, countInStock: newStock } : p));
      alert("Stock updated!");
    } catch (err) {
      alert("Failed to update stock");
    }
  };

  const updateMembershipStatus = async (requestId, status) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/membership-requests/${requestId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRequests(prev => prev.map(r => r._id === requestId ? res.data : r));
    } catch (err) {
      alert("Failed to update membership request status");
    }
  };

  // --- COMPUTED DATA (ANALYTICS) ---
  const stats = useMemo(() => {
    if (!Array.isArray(orders)) return { totalRevenue: 0, totalOrders: 0, pendingOrders: 0, lowStockItems: 0, topProducts: [] };

    const totalRevenue = orders.reduce((acc, o) => {
        if (o.status !== 'Cancelled') {
            return acc + (o.amount || 0); 
        }
        return acc;
    }, 0);

    const totalOrders = orders.length;
    const pendingOrders = orders.filter(o => o.status === 'Processing' || o.status === 'Pending Verification').length;
    const lowStockItems = products.filter(p => p.countInStock < 5).length;
    
    const productSales = {};
    orders.forEach(order => {
      const items = order.orderItems || [];
      items.forEach(item => {
        const itemName = item?.title || item?.name;
        if (itemName) {
            productSales[itemName] = (productSales[itemName] || 0) + Number(item.quantity || 1);
        }
      });
    });

    const topProducts = Object.entries(productSales)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    return { totalRevenue, totalOrders, pendingOrders, lowStockItems, topProducts };
  }, [orders, products]);

  const pendingRequests = requests.filter(r => r.status === 'pending').length;

  // --- FILTERED ORDERS ---
  const filteredOrders = orders.filter(order => {
    const matchesTab = orderTab === 'All' || order.status === orderTab;
    
    const name = order.address?.fullName || '';
    const phone = order.address?.phone || '';
    const id = order._id || '';

    const matchesSearch = id.includes(searchTerm) || 
                          name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          phone.includes(searchTerm);

    return matchesTab && matchesSearch;
  });

  if (!token || !isAdminUser) {
    return (
      <main
        role="main"
        aria-label="Admin access denied"
        className="flex items-center justify-center h-screen bg-gray-50 text-gray-600 font-medium"
      >
        Access denied. You must be logged in as an administrator to view this page.
      </main>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-50 text-gray-500 font-medium">
      <RefreshCw className="animate-spin mr-2" /> Loading Admin Panel...
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100 font-sans relative">
      
      {/* SIDEBAR */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col shadow-xl z-20">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white tracking-wider">ADMIN PANEL</h1>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <SidebarItem 
            icon={<LayoutDashboard size={20} />} 
            label="Dashboard" 
            active={activeSection === 'dashboard'} 
            onClick={() => setActiveSection('dashboard')} 
          />
          <SidebarItem 
            icon={<ShoppingCart size={20} />} 
            label="Orders" 
            active={activeSection === 'orders'} 
            onClick={() => setActiveSection('orders')} 
            badge={stats.pendingOrders}
          />
          <SidebarItem 
            icon={<Package size={20} />} 
            label="Inventory" 
            active={activeSection === 'inventory'} 
            onClick={() => setActiveSection('inventory')} 
            alert={stats.lowStockItems > 0}
          />
          {/* ✅ NEW: REQUESTS SIDEBAR ITEM */}
          <SidebarItem 
            icon={<Users size={20} />} 
            label="Requests" 
            active={activeSection === 'requests'} 
            onClick={() => setActiveSection('requests')} 
            badge={pendingRequests > 0 ? pendingRequests : null}
          />
          <SidebarItem 
            icon={<BarChart3 size={20} />} 
            label="Analytics" 
            active={activeSection === 'analytics'} 
            onClick={() => setActiveSection('analytics')} 
          />
        </nav>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto">
        <header className="bg-white shadow-sm sticky top-0 z-10 px-8 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800 capitalize">{activeSection} Overview</h2>
          <div className="flex items-center gap-4">
             <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition" title="Refresh Data">
                <RefreshCw size={18} />
             </button>
          </div>
        </header>

        <div className="p-8">
          {/* --- VIEW: ORDERS --- */}
          {activeSection === 'orders' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Toolbar */}
              <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex gap-2 bg-gray-100 p-1 rounded-md flex-wrap">
                   {['All', 'Pending Verification', 'Processing', 'Shipped', 'Delivered', 'Cancelled'].map(tab => (
                     <button
                       key={tab}
                       onClick={() => setOrderTab(tab)}
                       className={`px-3 py-1.5 text-xs font-semibold rounded ${
                         orderTab === tab ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                       }`}
                     >
                       {tab === 'Pending Verification' ? 'Verify' : tab}
                     </button>
                   ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                  <input 
                    type="text" 
                    placeholder="Search ID, Name or Phone..." 
                    className="pl-9 pr-4 py-2 border rounded-md text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold border-b">
                    <tr>
                      <th className="p-4">Order Info</th>
                      <th className="p-4">Customer</th>
                      <th className="p-4">Items</th>
                      <th className="p-4">Total</th>
                      <th className="p-4">Status & Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredOrders.map(order => {
                      const items = order.orderItems || [];
                      const paymentStatus = order.paymentStatus || (order.isPaid ? 'Paid' : 'Pending');
                      const shippingStatus = order.shippingStatus || 'Pending';
                      const hasShipment = Boolean(order.shiprocketOrderId || order.shiprocketShipmentId || order.shipmentId);
                      const canShip = order.status !== 'Cancelled' && order.status !== 'Delivered' && (order.paymentMethod === 'COD' || order.isPaid);
                      
                      return (
                      <tr key={order._id} className="hover:bg-gray-50">
                        <td className="p-4">
                          <span className="font-mono font-bold text-gray-700">#{order._id.slice(-6).toUpperCase()}</span>
                          <div className="text-xs text-gray-400 mt-1">{new Date(order.createdAt).toLocaleDateString()}</div>
                          {order.paymentMethod === 'UPI_MANUAL' && (
                             <span className="mt-1 inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">UPI (Check UTR)</span>
                          )}
                          <div className="mt-2 text-[10px] uppercase tracking-widest text-gray-400">Payment</div>
                          <div className="text-xs text-gray-600">
                            {order.paymentMethod} · {paymentStatus}
                          </div>
                        </td>
                        <td className="p-4">
                           <div className="font-medium">{order.address?.fullName}</div>
                           <div className="text-xs text-gray-500">{order.address?.phone}</div>
                        </td>
                        <td className="p-4 text-xs text-gray-600 max-w-xs">
                          {items.slice(0, 2).map((p, i) => (
                            <div key={i}>{p.title || p.name} <span className="text-gray-400">x{p.quantity}</span></div>
                          ))}
                          {items.length > 2 && <div className="text-gray-400 italic">+{items.length - 2} more...</div>}
                        </td>
                        <td className="p-4 font-bold text-gray-700">₹{order.amount}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                             <button 
                                onClick={() => setSelectedOrder(order)}
                                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition"
                                title="View Full Details"
                             >
                                <Eye size={16} />
                             </button>

                             <select 
                               value={order.status}
                               onChange={(e) => updateStatus(order._id, e.target.value)}
                               className={`text-xs border rounded p-1.5 font-semibold cursor-pointer ${
                                 order.status === 'Pending Verification' ? 'text-purple-600 bg-purple-50 border-purple-200' :
                                 order.status === 'Processing' ? 'text-orange-600 bg-orange-50 border-orange-200' :
                                 order.status === 'Shipped' ? 'text-blue-600 bg-blue-50 border-blue-200' :
                                 order.status === 'Delivered' ? 'text-green-600 bg-green-50 border-green-200' :
                                 'text-gray-600'
                               }`}
                             >
                               <option value="Pending Verification">Verify Payment</option>
                               <option value="Processing">Processing</option>
                               <option value="Shipped">Shipped</option>
                               <option value="Delivered">Delivered</option>
                               <option value="Cancelled">Cancelled</option>
                             </select>
                          </div>

                          <div className="mt-2 text-xs text-gray-500">
                            Shipping: <span className="font-semibold text-gray-700">{shippingStatus}</span>
                            {order.awbCode && (
                              <span className="ml-2">AWB: {order.awbCode}</span>
                            )}
                            {order.courierName && (
                              <span className="ml-2">({order.courierName})</span>
                            )}
                          </div>
                          
                          {/* Shiprocket Button */}
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                            {!canShip && order.paymentMethod !== 'COD' && !order.isPaid && (
                              <span className="text-orange-600 font-semibold">Payment Pending</span>
                            )}

                            {canShip && !hasShipment && (
                              <button 
                                  onClick={() => handleShip(order._id)}
                                  className="flex items-center gap-1 text-indigo-600 hover:underline"
                              >
                                  <Truck size={12} /> Create Shipment
                              </button>
                            )}

                            {canShip && hasShipment && !order.awbCode && !order.trackingUrl && (
                              <button 
                                  onClick={() => handleSyncTracking(order._id)}
                                  className="flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                  <Truck size={12} /> Sync Tracking
                              </button>
                            )}

                            {order.trackingUrl && (
                              <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-green-600 hover:underline"
                              >
                                <Truck size={12} /> Track Shipment
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* --- VIEW: INVENTORY --- */}
          {activeSection === 'inventory' && (
             <div className="bg-white rounded-lg shadow-sm border border-gray-200">
               <div className="p-6 border-b">
                 <h3 className="font-bold text-gray-700">Stock Management</h3>
               </div>
               <div className="overflow-x-auto">
                 <table className="w-full text-left text-sm">
                   <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold">
                     <tr>
                       <th className="p-4">Product</th>
                       <th className="p-4">Price</th>
                       <th className="p-4">Current Stock</th>
                       <th className="p-4">Actions</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-100">
                     {products.map(product => (
                       <tr key={product._id} className="hover:bg-gray-50">
                         <td className="p-4 flex items-center gap-3">
                           <img src={product.image} alt="" className="w-10 h-10 object-cover rounded bg-gray-100" />
                           <span className="font-medium text-gray-800">{product.name}</span>
                         </td>
                         <td className="p-4">₹{product.price}</td>
                         <td className="p-4">
                           <span className={`px-2 py-1 rounded text-xs font-bold ${
                             product.countInStock < 5 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                           }`}>
                             {product.countInStock} Units
                           </span>
                         </td>
                         <td className="p-4">
                           <div className="flex items-center gap-2">
                             <input 
                               type="number" 
                               defaultValue={product.countInStock}
                               min="0"
                               id={`stock-${product._id}`}
                               className="w-16 border rounded p-1 text-center text-sm"
                             />
                             <button 
                               onClick={() => {
                                 const val = document.getElementById(`stock-${product._id}`).value;
                                 handleStockUpdate(product._id, val);
                               }}
                               className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded transition"
                             >
                               <Save size={14} />
                             </button>
                           </div>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
             </div>
          )}

          {/* --- ✅ NEW VIEW: REQUESTS --- */}
          {activeSection === 'requests' && (
             <div className="bg-white rounded-lg shadow-sm border border-gray-200">
               <div className="p-6 border-b flex justify-between items-center">
                 <h3 className="font-bold text-gray-700">Membership Requests</h3>
                 <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">{requests.length} Total</span>
               </div>
               <div className="overflow-x-auto">
                 <table className="w-full text-left text-sm">
                   <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold">
                     <tr>
                       <th className="p-4">Name</th>
                       <th className="p-4">Phone</th>
                       <th className="p-4">Quantity/month</th>
                       <th className="p-4">Address</th>
                       <th className="p-4">Location</th>
                       <th className="p-4">Date</th>
                       <th className="p-4">Status</th>
                       <th className="p-4">Actions</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-100">
                     {requests.length > 0 ? requests.map((req) => (
                       (() => {
                         const latitude = req.locationLatitude !== null && req.locationLatitude !== undefined && req.locationLatitude !== ''
                           ? Number(req.locationLatitude)
                           : null;
                         const longitude = req.locationLongitude !== null && req.locationLongitude !== undefined && req.locationLongitude !== ''
                           ? Number(req.locationLongitude)
                           : null;
                         const accuracy = req.locationAccuracy !== null && req.locationAccuracy !== undefined && req.locationAccuracy !== ''
                           ? Number(req.locationAccuracy)
                           : null;
                         const hasLocation = Number.isFinite(latitude) && Number.isFinite(longitude);
                         const locationHref = hasLocation
                           ? `https://www.google.com/maps?q=${latitude},${longitude}`
                           : null;

                         return (
                       <tr key={req._id} className="hover:bg-gray-50">
                         <td className="p-4 font-medium text-gray-800">{req.fullName}</td>
                         <td className="p-4 text-gray-600">{req.phone}</td>
                         <td className="p-4 text-gray-600">{req.quantityPerMonthKg ? `${req.quantityPerMonthKg} kg` : 'N/A'}</td>
                         <td className="p-4 text-gray-600 max-w-xs truncate">{req.address}</td>
                         <td className="p-4 text-gray-600">
                           {hasLocation ? (
                             <div className="space-y-1">
                               <a
                                 href={locationHref}
                                 target="_blank"
                                 rel="noreferrer"
                                 className="text-blue-600 hover:underline font-medium"
                               >
                                 View Location
                               </a>
                               {Number.isFinite(accuracy) && (
                                 <div className="text-xs text-gray-500">Accuracy: {Math.round(accuracy)}m</div>
                               )}
                             </div>
                           ) : (
                             <span className="text-xs text-gray-400">Not shared</span>
                           )}
                         </td>
                         <td className="p-4 text-gray-500 text-xs">
                           {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : 'N/A'}
                         </td>
                         <td className="p-4">
                           <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wide ${
                             req.status === 'accepted' ? 'bg-green-100 text-green-700' :
                             req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                             'bg-yellow-100 text-yellow-700'
                           }`}>
                             {req.status || 'pending'}
                           </span>
                         </td>
                         <td className="p-4">
                           {req.status === 'pending' ? (
                             <div className="flex items-center gap-2">
                               <button
                                 onClick={() => updateMembershipStatus(req._id, 'accepted')}
                                 className="px-3 py-1 text-xs font-semibold rounded bg-green-600 text-white hover:bg-green-700 transition"
                               >
                                 Accept
                               </button>
                               <button
                                 onClick={() => updateMembershipStatus(req._id, 'rejected')}
                                 className="px-3 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 transition"
                               >
                                 Reject
                               </button>
                             </div>
                           ) : (
                             <span className="text-xs text-gray-400">No actions</span>
                           )}
                         </td>
                       </tr>
                         );
                       })()
                     )) : (
                       <tr>
                         <td colSpan="7" className="p-8 text-center text-gray-400">
                           No membership requests found.
                         </td>
                       </tr>
                     )}
                   </tbody>
                 </table>
               </div>
             </div>
          )}

          {/* --- VIEW: DASHBOARD (Stats) --- */}
          {activeSection === 'dashboard' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} color="blue" />
                <StatCard title="Total Orders" value={stats.totalOrders} color="purple" />
                <StatCard title="Pending" value={stats.pendingOrders} color="orange" />
                <StatCard title="Requests" value={pendingRequests} color="red" />
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 max-w-lg">
                   <h3 className="font-bold text-gray-700 mb-4">Top Selling Products</h3>
                   <div className="space-y-4">
                      {stats.topProducts.map(([name, qty], idx) => (
                        <div key={idx} className="flex justify-between items-center border-b border-gray-50 pb-2">
                          <span className="text-sm text-gray-600">{name}</span>
                          <span className="font-bold text-gray-800">{qty} Sold</span>
                        </div>
                      ))}
                      {stats.topProducts.length === 0 && <p className="text-gray-400 text-sm">No sales yet.</p>}
                   </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* --- ORDER DETAILS MODAL --- */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Order #{selectedOrder._id.slice(-6).toUpperCase()}</h3>
                <p className="text-sm text-gray-500">{new Date(selectedOrder.createdAt).toLocaleString()}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-red-500">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 grid gap-6">

              {/* UTR Verification Box (Only for Manual UPI) */}
              {selectedOrder.paymentMethod === 'UPI_MANUAL' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="text-yellow-600 shrink-0" size={24} />
                      <div>
                          <h4 className="font-bold text-yellow-800 text-sm uppercase">Verify Payment (UPI)</h4>
                          <p className="text-sm text-yellow-800 mt-1">Customer provided Transaction ID (UTR):</p>
                          <div className="mt-2 bg-white border border-yellow-300 px-3 py-2 rounded font-mono font-bold text-gray-800 tracking-wider select-all">
                              {selectedOrder.paymentResult?.id || "NO ID PROVIDED"}
                          </div>
                      </div>
                  </div>
              )}
              
              {/* Top Row: Status & Amount */}
              <div className="flex flex-wrap gap-4 justify-between bg-blue-50 p-4 rounded-lg border border-blue-100">
                 <div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">Status</span>
                    <p className="font-semibold text-blue-900">{selectedOrder.status}</p>
                 </div>
                 <div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">Payment</span>
                    <p className="font-semibold text-blue-900">
                      {selectedOrder.paymentMethod} · {selectedOrder.paymentStatus || (selectedOrder.isPaid ? 'Paid' : 'Pending')}
                    </p>
                 </div>
                 <div className="text-right">
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">Total Amount</span>
                    <p className="text-xl font-bold text-blue-900">₹{selectedOrder.amount}</p>
                 </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <h4 className="text-xs font-bold uppercase text-gray-500 mb-2">Payment Details</h4>
                  <div className="text-sm text-gray-700">
                    <div>Provider: <span className="font-semibold">{selectedOrder.paymentProvider || 'N/A'}</span></div>
                    <div>Status: <span className="font-semibold">{selectedOrder.paymentStatus || (selectedOrder.isPaid ? 'Paid' : 'Pending')}</span></div>
                    {selectedOrder.razorpayPaymentId && (
                      <div>Razorpay ID: <span className="font-mono">{selectedOrder.razorpayPaymentId}</span></div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <h4 className="text-xs font-bold uppercase text-gray-500 mb-2">Shipping Details</h4>
                  <div className="text-sm text-gray-700">
                    <div>Status: <span className="font-semibold">{selectedOrder.shippingStatus || 'Pending'}</span></div>
                    {selectedOrder.shiprocketOrderId && (
                      <div>Shiprocket Order: <span className="font-mono">{selectedOrder.shiprocketOrderId}</span></div>
                    )}
                    {(selectedOrder.shiprocketShipmentId || selectedOrder.shipmentId) && (
                      <div>Shipment ID: <span className="font-mono">{selectedOrder.shiprocketShipmentId || selectedOrder.shipmentId}</span></div>
                    )}
                    {selectedOrder.awbCode && (
                      <div>AWB: <span className="font-mono">{selectedOrder.awbCode}</span></div>
                    )}
                    {selectedOrder.courierName && (
                      <div>Courier: <span className="font-semibold">{selectedOrder.courierName}</span></div>
                    )}
                    {selectedOrder.trackingUrl && (
                      <div>
                        <a href={selectedOrder.trackingUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          Track Shipment
                        </a>
                      </div>
                    )}
                    {selectedOrder.expectedDeliveryDate && (
                      <div>ETA: <span className="font-semibold">{new Date(selectedOrder.expectedDeliveryDate).toLocaleDateString()}</span></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Grid: Address & Customer */}
              <div className="grid md:grid-cols-2 gap-6">
                 <div className="space-y-2">
                    <h4 className="font-bold text-gray-700 border-b pb-1">Customer Details</h4>
                    <p className="text-sm"><span className="font-medium">Name:</span> {selectedOrder.address?.fullName}</p>
                    <p className="text-sm"><span className="font-medium">Email:</span> {selectedOrder.address?.email || 'N/A'}</p>
                    <p className="text-sm"><span className="font-medium">Phone:</span> {selectedOrder.address?.phone}</p>
                 </div>
                 <div className="space-y-2">
                    <h4 className="font-bold text-gray-700 border-b pb-1">Shipping Address</h4>
                    <p className="text-sm text-gray-600">
                      {selectedOrder.address?.street}<br/>
                      {selectedOrder.address?.city}, {selectedOrder.address?.state}<br/>
                      {selectedOrder.address?.country} - <strong>{selectedOrder.address?.pincode}</strong>
                    </p>
                 </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="font-bold text-gray-700 border-b pb-2 mb-3">Order Items</h4>
                <div className="space-y-3">
                  {(selectedOrder.orderItems || []).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                       <div className="flex items-center gap-3">
                          {item.image && <img src={item.image} alt="" className="w-12 h-12 object-cover rounded border" />}
                          <div>
                             <p className="font-medium text-gray-800">{item.title}</p>
                             <p className="text-xs text-gray-500">Qty: {item.quantity} {item.variant && `| ${item.variant}`}</p>
                          </div>
                       </div>
                       <p className="font-medium">₹{item.price * item.quantity}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
               <button 
                  onClick={() => setSelectedOrder(null)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 text-sm font-medium hover:bg-gray-100"
               >
                  Close
               </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

// --- SUBCOMPONENTS ---
const SidebarItem = ({ icon, label, active, onClick, badge, alert }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all duration-200 ${
      active ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
    }`}
  >
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </div>
    <div className="flex gap-2">
      {alert && <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>}
      {badge > 0 && (
        <span className="bg-indigo-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </div>
  </button>
);

const StatCard = ({ title, value, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    red: "bg-red-50 text-red-600 border-red-200",
  };
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 relative overflow-hidden group hover:shadow-md transition">
      <div className={`absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition ${colorClasses[color].split(" ")[1]}`}>
         <ArrowUpRight size={48} />
      </div>
      <p className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2">{title}</p>
      <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
    </div>
  );
};

export default AdminDashboard;
